import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogoMark } from "./Logo";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, ListFilter, Scale, FileLock2, BellRing,
  Waypoints, Webhook, Settings, LogOut, ChevronRight, Activity, Users,
} from "lucide-react";

const items = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true, tid: "sidebar-observatory" },
  { to: "/app/decisions", label: "Decisions", icon: ListFilter, tid: "sidebar-decisions" },
  { to: "/app/policies", label: "Policies", icon: Scale, tid: "sidebar-policies" },
  { to: "/app/playground", label: "Gateway Playground", icon: Waypoints, tid: "sidebar-playground" },
  { to: "/app/drift", label: "Drift", icon: Activity, tid: "sidebar-drift" },
  { to: "/app/alerts", label: "Alerts", icon: BellRing, tid: "sidebar-alerts" },
  { to: "/app/audit", label: "Audit Bundles", icon: FileLock2, tid: "sidebar-audit" },
  { to: "/app/webhooks", label: "Webhooks", icon: Webhook, tid: "sidebar-webhooks" },
  { to: "/app/team", label: "Team", icon: Users, tid: "sidebar-team" },
  { to: "/app/settings", label: "Settings", icon: Settings, tid: "sidebar-settings" },
];

export default function DashboardShell({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen text-cream" style={{ background: "#0d1729" }}>
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-[248px] border-r border-white/10 bg-[#0A0E1A] flex flex-col">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <LogoMark size={36} />
            <div>
              <div className="font-display text-lg font-bold">Sentry</div>
              <div className="text-[10px] uppercase tracking-[.22em] text-cream/50">& Observatory</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 scroll-dark overflow-auto">
          {items.map(it => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.tid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
                  isActive ? "bg-[#FF6B35] text-white font-semibold" : "text-cream/75 hover:bg-white/5"
                }`
              }>
              <it.icon size={16} />
              <span>{it.label}</span>
              <ChevronRight size={14} className="ml-auto opacity-0 group-hover:opacity-50" />
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2 rounded-md bg-white/5">
            <div className="w-8 h-8 rounded-full bg-[#FF6B35] flex items-center justify-center font-bold text-white">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[11px] text-cream/50 truncate">{user?.tier} · {user?.email}</div>
            </div>
            <button data-testid="sidebar-logout" onClick={() => { logout(); nav("/"); }} className="p-1.5 rounded hover:bg-white/10">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="ml-[248px]">
        <div className="starfield-soft border-b border-white/10">
          <div className="px-8 py-6 flex items-start justify-between gap-6">
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-bold">{title}</h1>
              {subtitle && <p className="text-cream/60 text-sm mt-1">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-3">{actions}</div>
          </div>
        </div>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}
